import { request } from "undici";
import { z } from "zod";
import type { AuthProvider } from "../auth/types.js";
import { ApiError } from "../utils/errors.js";

const GUID =
  /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$(?![\s\S])/;
const ORIGIN =
  /^https:\/\/[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.(?:api\.)?crm[0-9]{0,2}\.dynamics\.com$(?![\s\S])/i;
const DEADLINE_MS = 15_000;
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const guid = (value: unknown): value is string => typeof value === "string" && GUID.test(value);
const own = (value: Record<string, unknown>, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key) ? value[key] : undefined;
const NOTICE =
  "EXPERIMENTAL native file-index status snapshot, not an ingestion trigger. A reported Completed status does not bind indexing to the current file bytes/hash or prove agent use, publication or answer quality. PartiallyCompleted is not complete. An unavailable status (including HTTP 404) does not prove indexing is in progress or identify its configuration cause. No retry, indexing/search change, upload, deletion or publication was performed.";

export const getCopilotKnowledgeFileReadinessInputSchema = z
  .object({
    agentId: z.string().regex(GUID),
    componentId: z.string().regex(GUID),
  })
  .strict();

export class CopilotKnowledgeFileReadinessError extends ApiError {
  constructor(
    readonly reason:
      | "input"
      | "configuration"
      | "authentication"
      | "identity"
      | "preflight"
      | "response"
      | "deadline"
      | "transport"
      | "http",
    status?: number,
    readonly phase?: "identity-preflight",
  ) {
    super(
      `Copilot knowledge-file readiness ${reason} check failed${status === undefined ? "" : ` (HTTP ${status})`}.${reason === "authentication" && phase === "identity-preflight" ? " A preverified selected-account identity or supported silent provider is unavailable. No token or HTTP request was attempted." : ""} No retry, sign-in, alternate route or indexing change was attempted.`,
      status ?? 502,
    );
    this.name = "CopilotKnowledgeFileReadinessError";
  }
}

type RecordStatus = "Completed" | "PartiallyCompleted" | "InProgress" | "Error";
type Readiness =
  "completed" | "partially-completed" | "in-progress" | "error" | "unknown" | "unavailable";
const statuses: Record<RecordStatus, Readiness> = {
  Completed: "completed",
  PartiallyCompleted: "partially-completed",
  InProgress: "in-progress",
  Error: "error",
};

// Microsoft Studio's public assets, observed 2026-09-07:
// https://content.powerapps.com/resource/makerx/static/js/module/2.7dc40098.chunk.js
// Modules 26765–26767 define this exact status POST/response and map HTTP404 to
// UI "indexing". We intentionally do NOT treat that UI fallback as readiness.
// https://content.powerapps.com/resource/makerx/static/js/module/592.b60ed609.chunk.js
// independently reads Results.entityAttributeRecordsIndexingStatus[0].recordsStatus.
// This private/native contract is experimental, not a documented public API SLA.
export class CopilotKnowledgeFileReadinessApi {
  private readonly origin: string;
  constructor(
    private readonly config: {
      authProvider: Pick<AuthProvider, "getCurrentAccount" | "tryGetTokenForResourceSilent">;
      environmentUrl: string;
    },
  ) {
    if (typeof config.environmentUrl !== "string" || !ORIGIN.test(config.environmentUrl))
      throw new CopilotKnowledgeFileReadinessError("configuration");
    this.origin = config.environmentUrl.toLowerCase();
  }

  async getReadiness(input: unknown) {
    const parsed = getCopilotKnowledgeFileReadinessInputSchema.safeParse(input);
    if (!parsed.success) throw new CopilotKnowledgeFileReadinessError("input");
    const agentId = parsed.data.agentId.toLowerCase();
    const componentId = parsed.data.componentId.toLowerCase();
    const auth = this.config.authProvider;
    const account = () => {
      try {
        const value: unknown = auth.getCurrentAccount();
        return object(value) ? value : null;
      } catch {
        throw new CopilotKnowledgeFileReadinessError("identity");
      }
    };
    const before = account();
    if (
      !before ||
      typeof before.username !== "string" ||
      !before.username ||
      before.username.length > 320 ||
      !guid(before.tenantId) ||
      !guid(before.localAccountId) ||
      !auth.tryGetTokenForResourceSilent
    )
      throw new CopilotKnowledgeFileReadinessError("authentication", undefined, "identity-preflight");
    const selected = {
      username: before.username,
      tenantId: before.tenantId,
      localAccountId: before.localAccountId,
      homeAccountId: before.homeAccountId,
      environment: before.environment,
    };
    const controller = new AbortController();
    const end = Date.now() + DEADLINE_MS;
    const check = () => {
      if (controller.signal.aborted || Date.now() >= end)
        throw new CopilotKnowledgeFileReadinessError("deadline");
      const now = account();
      if (
        !now ||
        now.username !== selected.username ||
        now.tenantId !== selected.tenantId ||
        now.localAccountId !== selected.localAccountId ||
        now.homeAccountId !== selected.homeAccountId ||
        now.environment !== selected.environment
      )
        throw new CopilotKnowledgeFileReadinessError("identity");
    };
    const safeDestroy = (body: { destroy(): unknown }) => {
      try {
        body.destroy();
      } catch {
        /* cleanup cannot replace a safe error */
      }
    };
    let timer: ReturnType<typeof setTimeout> | undefined;
    const work = async () => {
      let token: unknown;
      try {
        token = await auth.tryGetTokenForResourceSilent!(this.origin);
      } catch {
        throw new CopilotKnowledgeFileReadinessError("authentication");
      }
      check();
      if (typeof token !== "string" || !token || token.length > 65536 || /[\r\n]/.test(token))
        throw new CopilotKnowledgeFileReadinessError("authentication");
      const send = async (path: string, payload?: string) => {
        check();
        let response: Awaited<ReturnType<typeof request>> | undefined;
        try {
          response = await request(`${this.origin}${path}`, {
            method: payload === undefined ? "GET" : "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
              "OData-Version": "4.0",
              "OData-MaxVersion": "4.0",
              ...(payload === undefined ? {} : { "Content-Type": "application/json" }),
            },
            ...(payload === undefined ? {} : { body: payload }),
            signal: controller.signal,
            headersTimeout: Math.max(1, end - Date.now()),
            bodyTimeout: Math.max(1, end - Date.now()),
          });
          check();
          const status = response.statusCode;
          // Preflight denial/redirect bodies are never read. Status diagnostics
          // read only a small error object from the one fixed native endpoint.
          if (payload === undefined && status !== 200)
            throw new CopilotKnowledgeFileReadinessError("http", status);
          if (status >= 300 && status < 400)
            throw new CopilotKnowledgeFileReadinessError("http", status);
          const contentType = response.headers["content-type"];
          if (
            typeof contentType !== "string" ||
            !/^application\/(?:json|[a-z0-9.+-]+\+json)(?:\s*;|$)/i.test(contentType)
          ) {
            if (status !== 200) return { status, json: null, errorBody: "unavailable" as const };
            throw new CopilotKnowledgeFileReadinessError("response");
          }
          const cap = status === 200 ? 1024 * 1024 : 4096;
          const chunks: Buffer[] = [];
          let size = 0;
          for await (const chunk of response.body) {
            check();
            const bytes = Buffer.from(chunk as Uint8Array);
            size += bytes.length;
            if (size > cap) {
              if (status !== 200) return { status, json: null, errorBody: "truncated" as const };
              throw new CopilotKnowledgeFileReadinessError("response");
            }
            chunks.push(bytes);
          }
          check();
          let json: unknown;
          try {
            json = JSON.parse(Buffer.concat(chunks).toString("utf8"));
          } catch {
            if (status !== 200) return { status, json: null, errorBody: "unavailable" as const };
            throw new CopilotKnowledgeFileReadinessError("response");
          }
          return { status, json, errorBody: status === 200 ? null : ("read" as const) };
        } catch (error) {
          if (error instanceof CopilotKnowledgeFileReadinessError) throw error;
          check();
          // Optional error-body diagnostics must not erase an already received
          // HTTP failure. Never trust a partial JSON prefix after stream failure.
          if (payload !== undefined && response && response.statusCode !== 200)
            return { status: response.statusCode, json: null, errorBody: "unavailable" as const };
          throw new CopilotKnowledgeFileReadinessError("transport");
        } finally {
          if (response) safeDestroy(response.body);
        }
      };
      const bot = (await send(`/api/data/v9.2/bots(${agentId})?$select=botid`)).json;
      if (!object(bot) || !guid(bot.botid) || bot.botid.toLowerCase() !== agentId)
        throw new CopilotKnowledgeFileReadinessError("preflight");
      const row = (
        await send(
          `/api/data/v9.2/botcomponents(${componentId})?$select=botcomponentid,_parentbotid_value,componenttype,filedata`,
        )
      ).json;
      if (
        !object(row) ||
        !guid(row.botcomponentid) ||
        row.botcomponentid.toLowerCase() !== componentId ||
        !guid(row._parentbotid_value) ||
        row._parentbotid_value.toLowerCase() !== agentId ||
        row.componenttype !== 14 ||
        !guid(row.filedata)
      )
        throw new CopilotKnowledgeFileReadinessError("preflight");
      const response = await send(
        "/api/copilot/v1.0/textdatarecordsindexingstatus",
        JSON.stringify({
          entityAttributeRecordsParameters: [
            { entityName: "botcomponent", attributeName: "filedata", recordIds: [componentId] },
          ],
        }),
      );
      check();
      const base = {
        agentId,
        componentId,
        fileMetadataVerified: true,
        httpStatus: response.status,
        notice: NOTICE,
      };
      if (response.status !== 200) {
        const error = response.json;
        const serviceErrorCode =
          object(error) && error.errorCode === "DataverseCopilotException"
            ? "DataverseCopilotException"
            : null;
        const reason =
          response.status === 404 &&
          serviceErrorCode &&
          object(error) &&
          error.errorMessage === "Invalid request. Entity: botcomponent not found in index."
            ? "entity_not_in_index"
            : "unavailable";
        return {
          ...base,
          querySucceeded: false,
          readiness: "unavailable" as Readiness,
          recordStatus: null,
          serviceErrorCode,
          reason,
          errorBody: response.errorBody,
        };
      }
      const json = response.json;
      // Native module26765 refuses a root errorMessage before considering
      // Results. Also conservatively refuse errorCode-only envelope ambiguity.
      // Null/malformed markers must not promote contradictory results to Completed.
      if (
        object(json) &&
        ["errorMessage", "errorCode"].some((key) => Object.prototype.hasOwnProperty.call(json, key))
      )
        throw new CopilotKnowledgeFileReadinessError("response");
      const results = object(json) ? own(json, "Results") : undefined;
      const entries = object(results)
        ? own(results, "entityAttributeRecordsIndexingStatus")
        : undefined;
      if (!Array.isArray(entries) || entries.length !== 1 || !object(entries[0]))
        throw new CopilotKnowledgeFileReadinessError("response");
      const entry = entries[0];
      // These labels are not used by the published client, but if supplied must
      // not contradict the exact one-entity/one-attribute request.
      if (
        (own(entry, "entityName") !== undefined && entry.entityName !== "botcomponent") ||
        (own(entry, "attributeName") !== undefined && entry.attributeName !== "filedata")
      )
        throw new CopilotKnowledgeFileReadinessError("response");
      const records = own(entry, "recordsStatus");
      if (
        !object(records) ||
        Object.keys(records).length !== 1 ||
        !Object.prototype.hasOwnProperty.call(records, componentId) ||
        !object(records[componentId])
      )
        throw new CopilotKnowledgeFileReadinessError("response");
      const record = records[componentId];
      const value = own(record, "status");
      const recordStatus =
        typeof value === "string" &&
        value.length <= 32 &&
        Object.prototype.hasOwnProperty.call(statuses, value)
          ? (value as RecordStatus)
          : null;
      return {
        ...base,
        querySucceeded: true,
        readiness: recordStatus ? statuses[recordStatus] : ("unknown" as Readiness),
        recordStatus,
        serviceErrorCode: null,
        reason: recordStatus ? null : "unrecognized_record_status",
        errorBody: null,
      };
    };
    const deadline = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new CopilotKnowledgeFileReadinessError("deadline"));
      }, DEADLINE_MS);
    });
    try {
      return await Promise.race([work(), deadline]);
    } finally {
      if (timer) clearTimeout(timer);
      controller.abort();
    }
  }
}
